import PropTypes from "prop-types"
import React, { useEffect, useState, useRef } from "react"

import {
  Row,
  Col,
  CardBody,
  Card,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"

//redux
import { useSelector, useDispatch } from "react-redux"
// import { createSelector } from "reselect"
import { Link, useNavigate } from "react-router-dom"
import withRouter from "components/Common/withRouter"

// Formik validation
import * as Yup from "yup"
import { useFormik } from "formik"

import logo from "assets/images/logo.svg"
import { loginUser } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import Cookies from "js-cookie"

const Login = props => {
  const { t } = useTranslation()
  //meta title
  document.title = t("Login | Sign Burger")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [rememberMe, setRememberMe] = useState(false)
  const [lockoutSeconds, setLockoutSeconds] = useState(0)
  // Which of the two distinct causes lockoutSeconds currently reflects —
  // an actual account lockout, or the IP-based login rate limiter tripping.
  // The inline countdown text below must match whichever one it is, instead
  // of always saying "Account temporarily blocked" (which is wrong when the
  // real cause is the rate limiter, not the account).
  const [lockoutKind, setLockoutKind] = useState("ACCOUNT_LOCKED")
  const [lockedUntilMs, setLockedUntilMs] = useState(null)
  const [serverOffsetMs, setServerOffsetMs] = useState(0)
  const lockIntervalRef = useRef(null)

  const formatRemaining = seconds => {
    if (!seconds || seconds <= 0) return ""
    if (seconds < 60) {
      return seconds === 1
        ? t("second_singular")
        : t("second_plural", { count: seconds })
    }
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    const minsStr = mins === 1 ? t("minute_singular") : t("minute_plural", { count: mins })
    if (secs === 0) return minsStr
    const secsStr = secs === 1 ? t("second_singular") : t("second_plural", { count: secs })
    return `${minsStr} ${secsStr}`
  }

  const startServerSyncedCountdown = (lockedMs, serverTime) => {
    // clear any existing interval
    if (lockIntervalRef.current) {
      clearInterval(lockIntervalRef.current)
      lockIntervalRef.current = null
    }
    if (!lockedMs || !serverTime) return
    const offset = serverTime - Date.now()
    setLockedUntilMs(lockedMs)
    setServerOffsetMs(offset)
    const computeRemaining = () => Math.max(0, Math.ceil((lockedMs - (Date.now() + offset)) / 1000))
    // set initial
    setLockoutSeconds(computeRemaining())
    // start interval
    lockIntervalRef.current = setInterval(() => {
      const rem = computeRemaining()
      setLockoutSeconds(rem)
      if (rem <= 0) {
        clearInterval(lockIntervalRef.current)
        lockIntervalRef.current = null
        setLockedUntilMs(null)
        setServerOffsetMs(0)
      }
    }, 1000)
  }

  useEffect(() => {
    return () => {
      if (lockIntervalRef.current) {
        clearInterval(lockIntervalRef.current)
      }
    }
  }, [])

  const handleRememberMeChange = e => {
    setRememberMe(!rememberMe)
    if (!rememberMe) {
      Cookies.remove("rememberedEmail")
      Cookies.remove("rememberedPassword")
    }
  }

  const validation = useFormik({
    // enableReinitialize : use this  flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: values => {
      if (rememberMe) {
        Cookies.set("rememberedEmail", values.email, { expires: 30 })
        Cookies.set("rememberedPassword", values.password, { expires: 30 })
      } else {
        Cookies.remove("rememberedEmail")
        Cookies.remove("rememberedPassword")
      }

      // dispatch(loginUser(values, props.router.navigate));
      const payload = Object.assign({}, values, { history: props.router.navigate })
      dispatch(loginUser(payload))
        .then(action => {
          const { statusCode, status, msg, data, errorCode } = (action && action.payload) || {}
          if (statusCode === 200 && status) {
            setLockoutSeconds(0)
            dispatch(showAlert({ text: msg, type: "success" }))
          } else {
            if (errorCode === "ACCOUNT_LOCKED" || errorCode === "RATE_LIMITED") {
              // Build the message entirely from the dynamic time — never
              // prepend the server's static `msg` ("...after 15 minutes.")
              // in front of it, or the user sees both a wrong hardcoded
              // duration AND the real one in the same sentence.
              const translationKey =
                errorCode === "ACCOUNT_LOCKED"
                  ? "Account temporarily blocked. Please try again after {{time}}."
                  : "Too many login attempts. Please try again after {{time}}."
              setLockoutKind(errorCode)
              if (data && data.lockedUntil && data.serverTime) {
                startServerSyncedCountdown(data.lockedUntil, data.serverTime)
                const remainingSeconds = Math.max(0, Math.ceil((data.lockedUntil - data.serverTime) / 1000))
                const lockoutText = t(translationKey, { time: formatRemaining(remainingSeconds) })
                dispatch(showAlert({ text: lockoutText, type: "error" }))
              } else {
                const remainingSeconds = (data && data.remainingSeconds) || 0
                setLockoutSeconds(remainingSeconds)
                const lockoutText =
                  remainingSeconds > 0
                    ? t(translationKey, { time: formatRemaining(remainingSeconds) })
                    : msg
                dispatch(showAlert({ text: lockoutText, type: "error" }))
              }
            } else if (errorCode === "OTP_RESEND_COOLDOWN" || errorCode === "OTP_SEND_LIMITED") {
              // These come from the login-OTP send throttle (correct
              // password, but a new OTP can't be sent yet) — NOT an account
              // lockout, so this must stay out of the ACCOUNT_LOCKED/
              // RATE_LIMITED branch above (which sets lockoutSeconds and
              // shows "Account temporarily blocked").
              // msg from the server is a static "...after 15 minutes." string;
              // data.remainingSeconds has the real, much shorter, wait time.
              const remainingSeconds = (data && data.remainingSeconds) || 0
              const cooldownText =
                remainingSeconds > 0
                  ? t("Too many OTP resend requests. Please try again after {{time}}.", {
                      time: formatRemaining(remainingSeconds),
                    })
                  : msg
              dispatch(showAlert({ text: cooldownText, type: "error" }))
            } else if (errorCode === "WRONG_PASSWORD") {
              const remaining = (data && data.remainingAttempts)
              const attemptsText =
                remaining !== undefined
                  ? t(
                      "{{msg}} {{remaining}} attempt(s) remaining before your account is temporarily locked.",
                      { msg, remaining }
                    )
                  : msg
              dispatch(showAlert({ text: attemptsText, type: "error" }))
            } else {
              dispatch(
                showAlert({
                  text:
                    msg ||
                    (action && action.error && action.error.message) ||
                    "Something went wrong. Please try again.",
                  type: "error",
                })
              )
            }
            const uuid = (data && data.uuid)
            if (uuid) {
              navigate(`/reset-password/${uuid}`)
            }
          }
        })
        .catch(() => {
          dispatch(
            showAlert({
              text: "Something went wrong. Please try again.",
              type: "error",
            })
          )
        })
    },
  })

  useEffect(() => {
    const sessionExpiredNotice = sessionStorage.getItem("sessionExpiredNotice")
    if (sessionExpiredNotice) {
      const noticeType =
        sessionStorage.getItem("sessionExpiredNoticeType") || "error"
      dispatch(showAlert({ text: sessionExpiredNotice, type: noticeType }))
      sessionStorage.removeItem("sessionExpiredNotice")
      sessionStorage.removeItem("sessionExpiredNoticeType")
    }

    const rememberedEmail = Cookies.get("rememberedEmail")
    const rememberedPassword = Cookies.get("rememberedPassword")
    if (rememberedEmail) {
      validation.setFieldValue("email", rememberedEmail)
      validation.setFieldValue("password", rememberedPassword)
      setRememberMe(true)
    } else {
      setRememberMe(false)
    }
    // Run once on mount only. `validation` (from useFormik) is a new object
    // reference every render, so including it here re-runs the effect every
    // render, calling setFieldValue -> re-render -> effect again, forever.
  }, [dispatch])

  useEffect(() => {
    if (lockoutSeconds > 0) return undefined
    // when local countdown reaches zero, re-check server to ensure lock is cleared
    const email = validation.values.email || ""
    if (!email) return undefined
    let cancelled = false
    fetch(`/api/v1/auth/lock-status?email=${encodeURIComponent(email)}`)
      .then(res => res.json())
      .then(json => {
        if (cancelled) return
        const { errorCode, data } = json || {}
        if (errorCode === "ACCOUNT_LOCKED" && data && data.lockedUntil && data.serverTime) {
          setLockoutKind("ACCOUNT_LOCKED")
          startServerSyncedCountdown(data.lockedUntil, data.serverTime)
        } else if (errorCode === "ACCOUNT_LOCKED" && data && data.remainingSeconds) {
          setLockoutKind("ACCOUNT_LOCKED")
          setLockoutSeconds(data.remainingSeconds)
        } else {
          setLockoutSeconds(0)
        }
      })
      .catch(() => {
        if (cancelled) return
        // keep unlocked locally, server may be unreachable
        setLockoutSeconds(0)
      })
    return () => {
      cancelled = true
    }
  }, [lockoutSeconds])

  const { loading } = useSelector(state => state.Login)

  return (
    <React.Fragment>
      <div className="account-pages">
        <div className="login_form">
          <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col className="col-12">
                      <div className="text-primary p-4">
                        <h5 className="text-primary">{t("Welcome Back !")}</h5>
                        <p>{t("Sign in to continue to Sign.")}</p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/" className="logo-light-element">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            src={logo}
                            alt=""
                            className="rounded-circle"
                            height="34"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <div className="mb-3">
                        <Label className="form-label">{t("Email")}</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder={t("Email")}
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">{t("Password")}</Label>
                        <Input
                          name="password"
                          value={validation.values.password || ""}
                          type="password"
                          placeholder={t("Password")}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.password &&
                            validation.errors.password
                              ? true
                              : false
                          }
                        />
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="form-check" onClick={handleRememberMeChange}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="customControlInline"
                            checked={rememberMe}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="customControlInline"
                            onClick={e => {
                              e.preventDefault()
                              handleRememberMeChange(e)
                            }}
                          >
                            {t("Remember me")}
                          </label>
                        </div>
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                          disabled={loading || lockoutSeconds > 0}
                        >
                          {t("Log In")}
                        </button>
                      </div>

                      {lockoutSeconds > 0 && (
                        <div className="text-danger small text-center mt-2" role="alert">
                          {t(
                            lockoutKind === "ACCOUNT_LOCKED"
                              ? "Account temporarily blocked. Please try again after {{time}}."
                              : "Too many login attempts. Please try again after {{time}}.",
                            { time: formatRemaining(lockoutSeconds) }
                          )}
                        </div>
                      )}

                      <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-muted">
                          <i className="mdi mdi-lock me-1" />
                          {t("Forgot your password?")}
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
        </div>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
